import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { listAPIKeys, createAPIKey, revokeAPIKey, APIKey } from '../../api/apiKeysApi';

interface AppTheme {
  name?: string;
  codePanel?: { background?: string; border?: string; text?: string };
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function APIKeysManager() {
  const { theme } = useTheme() as { theme: AppTheme };
  const isDark = theme.name === 'dark';

  const border = theme.codePanel?.border ?? (isDark ? '#374151' : '#e5e7eb');
  const bg = theme.codePanel?.background ?? (isDark ? '#0f172a' : '#ffffff');
  const text = theme.codePanel?.text ?? (isDark ? '#e5e7eb' : '#111827');
  const inputBg = isDark ? '#0b1220' : '#f9fafb';

  const [keys, setKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newKey, setNewKey] = useState<string | null>(null);

  const [revoking, setRevoking] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setKeys(await listAPIKeys());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError('');
    setNewKey(null);
    setCreating(true);
    try {
      const res = await createAPIKey(name.trim(), expiresAt || undefined);
      setNewKey(res.key);
      setName('');
      setExpiresAt('');
      await load();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    setRevoking(id);
    try {
      await revokeAPIKey(id);
      setKeys((prev) => prev.filter((k) => k.id !== id));
    } catch {
      // non-critical — reload to sync
      await load();
    } finally {
      setRevoking(null);
    }
  }

  const inputStyle: React.CSSProperties = {
    padding: '7px 10px',
    borderRadius: 6,
    border: `1px solid ${border}`,
    background: inputBg,
    color: text,
    fontSize: 13,
  };

  const btnStyle = (danger = false, disabled = false): React.CSSProperties => ({
    padding: '6px 14px',
    borderRadius: 6,
    border: 'none',
    background: disabled ? '#9ca3af' : danger ? '#dc2626' : '#3b82f6',
    color: '#fff',
    fontSize: 13,
    cursor: disabled ? 'default' : 'pointer',
    fontWeight: 500,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Create form */}
      <div style={{ border: `1px solid ${border}`, borderRadius: 12, padding: 16, background: bg }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: text }}>Create API key</div>
        <form onSubmit={(e) => void handleCreate(e)} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ fontSize: 12, color: text, display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 180px' }}>
            Name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. CI pipeline"
              required
              style={inputStyle}
            />
          </label>
          <label style={{ fontSize: 12, color: text, display: 'flex', flexDirection: 'column', gap: 4 }}>
            Expires (optional)
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              style={inputStyle}
            />
          </label>
          <button type="submit" disabled={creating || !name.trim()} style={btnStyle(false, creating || !name.trim())}>
            {creating ? 'Creating…' : 'Generate'}
          </button>
        </form>
        {createError && (
          <p role="alert" style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{createError}</p>
        )}
        {newKey && (
          <div style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 8,
            background: isDark ? '#052e16' : '#f0fdf4',
            border: `1px solid ${isDark ? '#166534' : '#bbf7d0'}`,
            fontSize: 12,
            color: isDark ? '#86efac' : '#166534',
          }}>
            <strong>Copy this key — it will not be shown again:</strong>
            <div style={{
              fontFamily: 'monospace',
              marginTop: 6,
              wordBreak: 'break-all',
              userSelect: 'all',
              fontSize: 13,
            }}>
              {newKey}
            </div>
          </div>
        )}
      </div>

      {/* Key list */}
      <div style={{ border: `1px solid ${border}`, borderRadius: 12, padding: 16, background: bg }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: text }}>Your API keys</div>
        {error && <p style={{ color: '#dc2626', fontSize: 12 }}>{error}</p>}
        {loading && <p style={{ fontSize: 13, opacity: 0.6, color: text }}>Loading…</p>}
        {!loading && keys.length === 0 && !error && (
          <p style={{ fontSize: 13, opacity: 0.6, color: text }}>No API keys yet.</p>
        )}
        {keys.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: text }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${border}` }}>
                {['Name', 'Prefix', 'Created', 'Last used', 'Expires', ''].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, opacity: 0.7 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} style={{ borderBottom: `1px solid ${border}` }}>
                  <td style={{ padding: '8px 8px' }}>{k.name}</td>
                  <td style={{ padding: '8px 8px', fontFamily: 'monospace' }}>{k.prefix}…</td>
                  <td style={{ padding: '8px 8px', opacity: 0.7 }}>{formatDate(k.created_at)}</td>
                  <td style={{ padding: '8px 8px', opacity: 0.7 }}>{formatDate(k.last_used_at)}</td>
                  <td style={{ padding: '8px 8px', opacity: 0.7 }}>{formatDate(k.expires_at)}</td>
                  <td style={{ padding: '8px 8px' }}>
                    <button
                      onClick={() => void handleRevoke(k.id)}
                      disabled={revoking === k.id}
                      style={btnStyle(true, revoking === k.id)}
                    >
                      {revoking === k.id ? '…' : 'Revoke'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
